import { z } from 'zod';

export const GraphNode = z.object({
  id: z.string(),
  name: z.string(),
  val: z.number().optional().default(1),
  href: z.string().url().optional(),
  kind: z.enum(['post', 'article']).optional().catch(undefined)
});

export const GraphLink = z.object({
  source: z.union([z.string(), z.number()]).transform((v) => String(v)),
  target: z.union([z.string(), z.number()]).transform((v) => String(v))
});

export const GraphSchema = z.object({
  nodes: z.array(GraphNode),
  links: z.array(GraphLink),
  updatedAt: z.string().optional()
});

export type GraphData = z.infer<typeof GraphSchema>;

export function adaptGraphData(input: unknown): GraphData {
  const parsed = GraphSchema.safeParse(input);
  if (!parsed.success) {
    if (import.meta.env?.DEV) {
      console.warn('Graph schema mismatch', parsed.error.flatten());
    }
    // Try a minimal adapter if shapes differ slightly
    const maybe = input as any;
    const nodes = Array.isArray(maybe?.nodes)
      ? maybe.nodes.map((n: any) => ({
          id: String(n.id),
          name: String(n.name ?? n.id),
          val: Number(n.val ?? 1),
          href: typeof n.href === 'string' ? n.href : undefined,
          kind: GraphNode.shape.kind.parse(n.kind)
        }))
      : [];
    const links = Array.isArray(maybe?.links)
      ? maybe.links.map((l: any) => ({ source: String(l.source), target: String(l.target) }))
      : [];
    return { nodes, links, updatedAt: maybe?.updatedAt };
  }
  return parsed.data;
}

export type IdType = 'post' | 'page' | 'tag' | 'external' | 'unknown';

export function getIdType(id: string): IdType {
  const sep = id.indexOf(':');
  if (sep > 0) {
    const ns = id.slice(0, sep);
    if (ns === 'post') return 'post';
    if (ns === 'page') return 'page';
    if (ns === 'tag') return 'tag';
    if (ns === 'external') return 'external';
  }
  return 'unknown';
}

export type DisplayType = IdType | 'article';

export const NODE_STYLES = {
  post: { label: 'post', color: '#2563eb' },
  article: { label: 'article', color: '#c026d3' },
  page: { label: 'page', color: '#16a34a' },
  tag: { label: 'tag', color: '#d97706' },
  external: { label: 'external', color: '#6b7280' },
  unknown: { label: 'unknown', color: '#6b7280' }
} as const satisfies Record<DisplayType, { label: string; color: string }>;

export function getNodeDisplayType(node: Pick<GraphData['nodes'][number], 'id' | 'kind'>): DisplayType {
  const type = getIdType(node.id);
  return type === 'post' ? node.kind ?? 'post' : type;
}

export function colorForType(type: DisplayType): string {
  return NODE_STYLES[type].color;
}

export function getNodeColors(data: GraphData): Map<string, string> {
  return new Map(data.nodes.map((node) => [node.id, colorForType(getNodeDisplayType(node))]));
}

export function deriveUrl(id: string): string | null {
  // Try environment variable first (for build-time configuration)
  let origin = import.meta.env?.VITE_PUBLIC_ORIGIN as string | undefined;
  
  // Fallback to runtime detection for site to be used
  if (!origin && typeof window !== 'undefined') {
    origin = window.location.origin;
  }
  
  const [, slug] = id.split(':');
  
  if (!origin) {
    if (import.meta.env?.DEV) {
      console.warn('Cannot determine origin for URL derivation. ID:', id);
    }
    return null;
  }
  
  if (!slug) {
    if (import.meta.env?.DEV) {
      console.warn('No slug found in ID:', id);
    }
    return null;
  }
  
  const type = getIdType(id);
  if (type === 'tag') return `${origin.replace(/\/$/, '')}/tag/${slug}/`;
  if (type === 'post' || type === 'page') return `${origin.replace(/\/$/, '')}/${slug}/`;
  if (type === 'external') {
    try {
      const decoded = decodeURIComponent(slug);
      // Validate it looks like a URL
      const u = new URL(decoded);
      return u.toString();
    } catch {
      return null;
    }
  }
  return null;
}

export function isExternalHref(href: string): boolean {
  try {
    const url = new URL(href);
    if (typeof window === 'undefined') return false;
    const own = new URL(window.location.origin);
    return url.origin !== own.origin;
  } catch {
    // If not a valid URL, treat as internal path
    return false;
  }
}
